import {match} from 'ts-pattern';

import {useRef} from 'react';

import {isNil} from 'ramda';

import {CheckoutNotificationButton, useCallNotificationActionMutation} from '@dms/api/sales';
import {handleApiError} from '@dms/shared';

import {isSafeRelativeApiPath, isSafeUrl, useNavigate} from 'shared/web';

interface UseNotificationButtonActionProps {
  checkoutId: string;
  paymentId: string;
}

export function useNotificationButtonAction(props: UseNotificationButtonActionProps) {
  const navigate = useNavigate();
  const [callNotificationAction] = useCallNotificationActionMutation();
  const inFlightActionsRef = useRef<Set<string>>(new Set());

  const handleNotificationButtonClick = (button: CheckoutNotificationButton) =>
    match(button.action)
      .with('NAVIGATE', () => {
        const url = button.navigateUrl?.trim();

        if (isNil(url) || !isSafeUrl(url)) {
          return;
        }

        match(button.actionTarget)
          .with('NEW_TAB', () => window.open(url, '_blank', 'noopener,noreferrer'))
          .otherwise(() => navigate(url));
      })
      .with('API_CALL', () => {
        if (isNil(button.apiCall) || !isSafeRelativeApiPath(button.apiCall.path)) {
          return;
        }

        const apiCall = button.apiCall;
        const actionKey = `${apiCall.method}:${apiCall.path}:${JSON.stringify(apiCall.payload)}`;

        if (inFlightActionsRef.current.has(actionKey)) {
          return;
        }
        inFlightActionsRef.current.add(actionKey);

        const invalidateScope = match(button.onSuccess)
          .with('CHECKOUT_PAYMENT_REFRESH', () => 'paymentsList' as const)
          .with('CHECKOUT_REFRESH', () => 'checkout' as const)
          .otherwise(() => undefined);

        callNotificationAction({
          path: apiCall.path,
          method: apiCall.method,
          payload: apiCall.payload,
          checkoutId: props.checkoutId,
          paymentId: props.paymentId,
          invalidateScope,
        })
          .unwrap()
          .then(() => {
            if (button.onSuccess === 'PAGE_REFRESH') {
              window.location.reload();
            }
          })
          .catch(handleApiError)
          .finally(() => {
            inFlightActionsRef.current.delete(actionKey);
          });
      })
      .exhaustive();

  return {handleNotificationButtonClick};
}
